/**
 * purchaseRequestService.js
 * Centralised API service for the Purchase Requests module.
 * Base endpoint: /api/purchase-requests
 */

import { API_URL } from '../utils/Constants/URLS'

const BASE = `${API_URL}purchase-requests`

const authHeaders = (token) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
})

/**
 * GET /api/purchase-requests
 * @param {string} token
 * @param {Object} params - { search, approvalStatus, page, limit, sortBy, sortOrder }
 */
export const fetchPurchaseRequests = async (token, params = {}) => {
    const qs = []
    // encodeURIComponent uses %20 for spaces (not +) — better for backend search parsers
    if (params.search)          qs.push(`search=${encodeURIComponent(params.search)}`)
    if (params.approvalStatus && params.approvalStatus !== 'all')
                                qs.push(`approvalStatus=${encodeURIComponent(params.approvalStatus)}`)
    if (params.page)            qs.push(`page=${encodeURIComponent(params.page)}`)
    if (params.limit)           qs.push(`limit=${encodeURIComponent(params.limit)}`)
    if (params.sortBy)          qs.push(`sortBy=${encodeURIComponent(params.sortBy)}`)
    if (params.sortOrder)       qs.push(`sortOrder=${encodeURIComponent(params.sortOrder)}`)

    const res = await fetch(`${BASE}?${qs.join('&')}`, {
        method: 'GET',
        headers: authHeaders(token),
    })
    if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.message || `Error ${res.status}`)
    }
    return res.json()
}

/**
 * GET /api/purchase-requests/:id
 * @param {string} token
 * @param {string} id
 */
export const fetchPurchaseRequestById = async (token, id) => {
    const res = await fetch(`${BASE}/${id}`, {
        method: 'GET',
        headers: authHeaders(token),
    })
    if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.message || `Error ${res.status}`)
    }
    return res.json()
}

/**
 * POST /api/purchase-requests
 * @param {string} token
 * @param {Object} payload - { salesOrder?, lineItems }
 */
export const createPurchaseRequest = async (token, payload) => {
    const res = await fetch(`${BASE}`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
    })
    if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.message || `Error ${res.status}`)
    }
    return res.json()
}

/**
 * PATCH /api/purchase-requests/:id
 * Used for updating line items OR approving/rejecting.
 * @param {string} token
 * @param {string} id
 * @param {Object} payload - e.g. { approvalStatus: 'approved' } or { approvalStatus: 'rejected', rejectionReason: '...' }
 */
export const updatePurchaseRequest = async (token, id, payload) => {
    const res = await fetch(`${BASE}/${id}`, {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
    })
    if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.message || `Error ${res.status}`)
    }
    return res.json()
}

/**
 * Fetch Sales Orders for the searchable dropdown in Create PR form.
 * Uses the existing sales-order search endpoint.
 * @param {string} token
 * @param {string} query
 * @param {Object} userDetails - from redux store
 */
export const searchSalesOrders = async (token, query, userDetails = {}) => {
    let qs = `?limit=20`
    if (userDetails?.role === 'executive') {
        qs += `&businessLine=${userDetails.businessLine}&leadCreatedBy=${userDetails.id}`
    } else if (userDetails?.role === 'manager') {
        qs += `&businessLine=${userDetails.businessLine}`
    }
    if (query) qs += `&search=${encodeURIComponent(query)}`

    const res = await fetch(`${API_URL}leads/sales-orders${qs}`, {
        headers: authHeaders(token),
    })
    if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.message || `Error ${res.status}`)
    }
    return res.json()
}

/**
 * PATCH /api/leads/sales-orders/:id
 * Used to update SO fields like lineItems purchasedQuantity.
 */
export const updateSalesOrder = async (token, id, payload) => {
    const res = await fetch(`${API_URL}leads/sales-orders/${id}`, {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
    })
    if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.message || `Error ${res.status}`)
    }
    return res.json()
}
